import { useQuery } from '@tanstack/react-query'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Processing from 'ext-ui/components/Processing/Processing'
import React, {
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { OrgUpdateAwaiterContext } from 'src/app/App'
import DynamicSaasOnboardingPricingTiers from 'src/app/Onboarding/DynamicSaasOnboardingPricingTiers'
import PricingPageHero from 'src/app/Onboarding/PricingPageHero'
import { createPricingPresentation } from 'src/app/Onboarding/pricingPresentation'
import PromotionSavingsHero, {
  PromotionBillingCycle,
} from 'src/app/Onboarding/PromotionSavingsHero'
import SaasOnboardingPricingTiers, {
  SaasOnboardingPricingTiersProps,
} from 'src/app/Onboarding/SaasOnboardingPricingTiers'
import ConnectPhysicalCardModal from 'src/component-modal/ConnectPhysicalCardModal/ConnectPhysicalCardModal'
import EditCardDetailsModal from 'src/component-modal/EditCardDetailsModal/EditCardDetailsModal'
import BrandAgnosticFooter from 'src/components/BrandAgnosticFooter/BrandAgnosticFooter'
import BrandPrimaryLogo from 'src/components/BrandPrimaryLogo/BrandPrimaryLogo'
import { AstradaBinData } from 'src/context/AstradaContext/AstradaContext'
import useAstrada from 'src/context/AstradaContext/useAstrada'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { SupportedBrands } from 'src/enums'
import { useQueryParams } from 'src/hooks'
import useChargebeeCheckout, {
  useShouldEmbedChargebeeCheckout,
} from 'src/hooks/useChargebeeCheckout'
import { useCurrencyFormatter } from 'src/localization/useCurrencyFormatter'
import { useNullSafeLocale } from 'src/localization/useNullSafeLocale'
import { useTranslate } from 'src/localization/useTranslate'
import {
  BillingCycle,
  hasEstablishedOnboardingSubscription,
  SaasTierType,
} from 'src/models/organizations.models'
import { UserFeature } from 'src/models/user.models'
import { connectedReplace } from 'src/navigation/Lnk'
import { getStringQueryParam } from 'src/navigation/queryParams'
import {
  accountOverviewUrl,
  homeUrl,
  onboardingCardUrl,
} from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { pricingPlanQueries } from 'src/queries/organizations/pricingPlans/pricingPlans.queries'
import { isProPricingPlan } from 'src/queries/organizations/pricingPlans/pricingPlans.utils'
import {
  usePostCreateSubscription,
  usePostEnterpriseRequest,
  useQueryOrganizationSaasTier,
  useQueryOrganizationSubscription,
} from 'src/queries/organizations/saas.queries'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { isPhysicalCardTransactionsFeedEntitled } from 'src/utils/physicalCardTransactionsFeed'

import style from './PricingPage.style'

export default function PricingPage(): ReactElement {
  const [planLoadingStates, setPlanLoadingStates] = useState<
    SaasOnboardingPricingTiersProps['buttonLoadingStates']
  >({
    starter: false,
    pro: false,
    enterprise: false,
  })

  const dispatch = useDispatch()
  const user = useCurrentUser()
  const { show_dynamic_pricing_page: showDynamicPricingPage } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const shouldEmbedChargebee = useShouldEmbedChargebeeCheckout()
  const { analytics } = useAnalyticsContext()
  const astrada = useAstrada()
  // The Ack modal and the Edit Card Details modal are mutually exclusive —
  // one shared toggle keeps them from ever stacking. `null` means neither.
  const [ackView, setAckView] = useState<'ack' | 'edit' | null>(null)
  // The plan tier the user just clicked, captured before the modal opens so
  // ReviewEnrollCard can render the matching SaaS plan summary. Reset on
  // dismiss/verify so re-entering the flow can't reuse stale state.
  const [pendingSaasTier, setPendingSaasTier] = useState<SaasTierType | null>(
    null,
  )
  // Captured alongside pendingSaasTier so ReviewEnrollCard can vary copy by
  // billing cycle (e.g., suppress the 30-day-free-trial pill on Pro annual).
  const [pendingBillingPeriod, setPendingBillingPeriod] = useState<
    'MONTHLY' | 'YEARLY' | null
  >(null)
  // Non-dismissable spinner modal shown during the post-subscription wait for
  // the SAAS_ORGANIZATION pusher event + /features refetch. Without it the
  // user would be staring at a blank pricing page while the selected CTA still
  // spins until the shared org-update timeout resolves.
  const [showCheckoutLoadingModal, setShowCheckoutLoadingModal] =
    useState<boolean>(false)
  const translate = useTranslate()
  const locale = useNullSafeLocale()
  const presentation = useMemo(
    () => createPricingPresentation(translate, locale),
    [locale, translate],
  )
  const currencyFormatter = useCurrencyFormatter()
  const queryParams = useQueryParams()
  const cardId = getStringQueryParam(queryParams['cardId'])
  const issuerIdFromUrl = getStringQueryParam(queryParams['issuerId'])

  const { mutate: postCreateSubscription } = usePostCreateSubscription()
  const {
    trigger: triggerCheckout,
    openCheckoutUrl,
    renderCheckout,
  } = useChargebeeCheckout()

  const { mutateAsync: sendPostEnterpriseRequest } = usePostEnterpriseRequest({
    showSuccessToast: false,
  })

  // Current saas tier used by the already-Pro click guard below.
  const { data: organizationTier } = useQueryOrganizationSaasTier({
    orgId: user?.organizationId,
  })
  const currentTier = organizationTier?.tier

  const awaitOrgUpdateOrTimeout = useContext(OrgUpdateAwaiterContext)
  const [dynamicPricingLoadingPlanId, setDynamicPricingLoadingPlanId] =
    useState<string | null>(null)
  const [promotionBillingCycle, setPromotionBillingCycle] =
    useState<PromotionBillingCycle>('YEARLY')
  const viewedPromotionIdsRef = useRef(new Set<string>())

  // Dynamic pricing page data fetching
  const organizationId = user?.organization?.id
  const isAstradaOnboarding = Boolean(astrada.onboarding)
  const pricingIssuerId =
    (isAstradaOnboarding ? astrada.binData?.issuerId : undefined) ??
    issuerIdFromUrl
  const astradaBin = isAstradaOnboarding
    ? astrada.cardData?.number.replace(/\D/g, '').slice(0, 6)
    : undefined
  const pricingPromotionBin =
    astradaBin && /^\d{6}$/.test(astradaBin) ? astradaBin : undefined

  const { data: pricingPlans, isLoading: pricingPlansLoading } = useQuery(
    pricingPlanQueries.list(organizationId, {
      issuerId: pricingIssuerId,
      skipRedirect: shouldEmbedChargebee,
      cardId,
      bin: cardId ? undefined : pricingPromotionBin,
    }),
  )

  const { data: featuresData, isFetched: featuresIsFetched } =
    useQueryUserFeatures(
      [
        UserFeature.SAAS_ENABLED,
        // TODO: Remove after 2026-07-07 — fetched for the redirect guard below (EX-39394)
        UserFeature.EARLY_SIGNUP_ELIGIBLE,
      ],
      {},
    )

  // Separate query so we can await a fresh value after subscription updates without
  // touching the SAAS_ENABLED gate above. The Chargebee subscription change
  // can flip PHYSICAL_CARD_TRANSACTIONS_FEED, so we re-pull /features before
  // rendering the ack modal — otherwise we'd hand ReviewEnrollCard a card
  // eligibility the backend will reject.
  const { refetch: refetchPhysicalCardTxnsFeedFeature } = useQueryUserFeatures(
    [UserFeature.PHYSICAL_CARD_TRANSACTIONS_FEED],
    {},
  )

  // Override surfaced to the ack modal when the post-checkout features call
  // says the user lost physical-feed eligibility. Cleared when the modal
  // closes so a re-entry into the flow starts from astrada's eligibility.
  const [eligibilityOverride, setEligibilityOverride] = useState<
    AstradaBinData['eligibility'] | null
  >(null)

  // TODO: Remove the EARLY_SIGNUP_ELIGIBLE clause after 2026-07-07 (EX-39394)
  const isPricingPageGateClosed =
    showDynamicPricingPage &&
    featuresIsFetched &&
    !featuresData?.SAAS_ENABLED &&
    !featuresData?.EARLY_SIGNUP_ELIGIBLE

  useEffect(() => {
    if (isPricingPageGateClosed) {
      dispatch(connectedReplace(homeUrl()))
    }
  }, [isPricingPageGateClosed])

  // Onboarding subscription guard: the onboarding / card-enrollment entries to
  // this page always carry a cardId or issuerId param (manage/upgrade uses a
  // bare /pricing/tiers), so their presence — or a live astrada onboarding
  // context — marks an onboarding view. If such a user already has a
  // subscription of any tier (including the free Starter plan — e.g. they
  // selected a plan, dropped out before enrolling their card, and came back),
  // don't show the tiers again or let them re-select/pay twice — send them to
  // finish card registration.
  const guardOrgId = user?.organizationId
  const isOnboardingPricingEntry = Boolean(
    cardId || issuerIdFromUrl || astrada.onboarding,
  )
  const subscriptionGuardEnabled =
    isOnboardingPricingEntry && Boolean(guardOrgId)
  const subscriptionGuardEvaluatedRef = useRef(false)
  const { data: subscriptionData, isFetched: subscriptionIsFetched } =
    useQueryOrganizationSubscription({
      orgId: guardOrgId,
      enabled: subscriptionGuardEnabled,
    })
  const isWaitingForSubscriptionGuard =
    subscriptionGuardEnabled && !subscriptionIsFetched
  const shouldRedirectForExistingSubscription =
    !subscriptionGuardEvaluatedRef.current &&
    subscriptionGuardEnabled &&
    subscriptionIsFetched &&
    hasEstablishedOnboardingSubscription(subscriptionData?.subscription)

  // One-shot: evaluate the guard against the subscription status *on arrival*
  // only. A subscription created later in this same session (the user
  // completing checkout here) must not retrigger the redirect — that success
  // path opens the enrollment ack modal instead.
  const [isRedirectingForSubscription, setIsRedirectingForSubscription] =
    useState(false)
  useEffect(() => {
    if (subscriptionGuardEvaluatedRef.current) return
    if (!subscriptionGuardEnabled || !subscriptionIsFetched) return
    subscriptionGuardEvaluatedRef.current = true
    if (shouldRedirectForExistingSubscription) {
      analytics.track('onboarding pricing subscription guard', {
        action: 'redirected',
        orgId: guardOrgId,
        userId: user?.id,
      })
      setIsRedirectingForSubscription(true)
      dispatch(connectedReplace(onboardingCardUrl()))
    }
  }, [
    subscriptionGuardEnabled,
    subscriptionIsFetched,
    shouldRedirectForExistingSubscription,
    analytics,
    dispatch,
    guardOrgId,
    user?.id,
  ])

  const sortedPlans = useMemo(() => {
    if (!pricingPlans?.plans) return []
    return [...pricingPlans.plans].sort(
      (a, b) => a.displayOrder - b.displayOrder,
    )
  }, [pricingPlans?.plans])

  const isNonExtendIssuer =
    !!pricingPlans?.partnerName &&
    pricingPlans?.partnerName.toLowerCase() !== SupportedBrands.Extend

  const activePromotion = pricingPlans?.promotion

  useEffect(() => {
    if (
      !showDynamicPricingPage ||
      !featuresIsFetched ||
      pricingPlansLoading ||
      isWaitingForSubscriptionGuard ||
      isPricingPageGateClosed ||
      shouldRedirectForExistingSubscription ||
      isRedirectingForSubscription ||
      !activePromotion ||
      viewedPromotionIdsRef.current.has(activePromotion.id)
    ) {
      return
    }

    viewedPromotionIdsRef.current.add(activePromotion.id)
    analytics.track('pricing promotion', {
      action: 'hero_viewed',
      campaign_id: activePromotion.id,
      surface: 'authenticated_pricing',
      billing_cycle: promotionBillingCycle,
    })
  }, [
    activePromotion,
    analytics,
    featuresIsFetched,
    isPricingPageGateClosed,
    isRedirectingForSubscription,
    isWaitingForSubscriptionGuard,
    pricingPlansLoading,
    promotionBillingCycle,
    shouldRedirectForExistingSubscription,
    showDynamicPricingPage,
  ])

  const redirect = (): void => {
    if (cardId !== undefined) {
      // Redirect to newly registered card on success
      dispatch(connectedReplace(accountOverviewUrl(cardId)))
    } else {
      // Redirect to home on success
      dispatch(connectedReplace(homeUrl()))
    }
  }

  const effectiveBinData =
    astrada.binData && eligibilityOverride
      ? { ...astrada.binData, eligibility: eligibilityOverride }
      : astrada.binData

  const ackSubmissionData =
    effectiveBinData && astrada.cardData && astrada.addressData
      ? {
          binData: effectiveBinData,
          cardData: astrada.cardData,
          addressData: astrada.addressData,
          issuerData: astrada.issuerData,
        }
      : undefined

  // Flag-gated escape hatch: show the Card Acknowledgements Modal pre-filled
  // from astrada context instead of redirecting. Requires astrada.onboarding
  // so stale astrada state from a regular register-card flow can't leak into
  // manage-subscription entries to /pricing/tiers.
  const shouldShowAckModalOnSuccess = Boolean(
    astrada.onboarding && ackSubmissionData,
  )

  // Read the latest value inside async checkout callbacks so an astrada reset
  // between click and success can't stale-close the captured boolean.
  const shouldShowAckModalOnSuccessRef = useRef(shouldShowAckModalOnSuccess)
  shouldShowAckModalOnSuccessRef.current = shouldShowAckModalOnSuccess

  const resetPlanLoadingStates = (): void => {
    setPlanLoadingStates({ starter: false, pro: false, enterprise: false })
    setDynamicPricingLoadingPlanId(null)
  }

  // Clear loading before opening the ack modal so the CTA behind it isn't
  // left spinning if the user backs out or verify stalls.
  const openAckModal = (): void => {
    resetPlanLoadingStates()
    setAckView('ack')
  }

  const handleAckModalDismiss = (): void => {
    setAckView(null)
    setEligibilityOverride(null)
    setPendingSaasTier(null)
    setPendingBillingPeriod(null)
    resetPlanLoadingStates()
  }

  const handleAckVerifyComplete = (completedCardId: string): void => {
    setAckView(null)
    setEligibilityOverride(null)
    setPendingSaasTier(null)
    setPendingBillingPeriod(null)
    astrada.reset()
    dispatch(connectedReplace(accountOverviewUrl(completedCardId)))
  }

  // Edit Card Details modal — opens via the pencil link in the Ack modal,
  // returns to the Ack modal on save (with merged values) or on dismiss
  // (without changes).
  const handleEditCardSubmit: React.ComponentProps<
    typeof EditCardDetailsModal
  >['onSubmit'] = (updated) => {
    astrada.updateState({
      cardData: updated.cardData,
      addressData: updated.addressData,
      issuerData: updated.issuerData,
    })
    setAckView('ack')
  }

  const handleEditCardDismiss = (): void => {
    setAckView('ack')
  }

  // Pulls /features and decides the ack modal's eligibility
  // (PHYSICAL_AND_VIRTUAL → VIRTUAL_ONLY when the physical-feed feature came
  // back disabled), then opens the modal. Awaited by every astrada-onboarding
  // success path so ReviewEnrollCard never receives an eligibility the
  // backend will reject — otherwise the downstream /physical call fails.
  // Always writes a fresh override (including null) so a stale value from a
  // previous attempt can't sneak through and show VIRTUAL_ONLY when the
  // refetched feature actually says physical is allowed.
  const openAckModalWithFeatureCheck = async (): Promise<void> => {
    const { data } = await refetchPhysicalCardTxnsFeedFeature()
    const physicalFeedEnabled = isPhysicalCardTransactionsFeedEntitled(data)
    const nextOverride: AstradaBinData['eligibility'] | null =
      !physicalFeedEnabled &&
      astrada.binData?.eligibility === 'PHYSICAL_AND_VIRTUAL'
        ? 'VIRTUAL_ONLY'
        : null
    setEligibilityOverride(nextOverride)
    setShowCheckoutLoadingModal(false)
    openAckModal()
  }

  const openAckModalAfterSaasOrganizationUpdate = async (): Promise<void> => {
    setShowCheckoutLoadingModal(true)
    try {
      const orgId = user?.organization?.id ?? user?.organizationId
      if (orgId) {
        await awaitOrgUpdateOrTimeout(orgId)
      }
      await openAckModalWithFeatureCheck()
    } finally {
      setShowCheckoutLoadingModal(false)
    }
  }

  const handleProCheckoutSuccess = async (): Promise<void> => {
    if (shouldShowAckModalOnSuccessRef.current) {
      // Chargebee's success callback fires when the iframe closes, but the
      // backend processes the subscription webhook asynchronously — and that
      // webhook is what flips PHYSICAL_CARD_TRANSACTIONS_FEED in /features.
      // Wait for the SAAS_ORGANIZATION pusher event the backend fires after
      // setSubscription completes; the shared timeout falls back to refetching
      // anyway. The loading modal keeps the user oriented during the wait.
      await openAckModalAfterSaasOrganizationUpdate()
    } else {
      // No ack modal to render, but downstream pages read the same /features
      // cache — refresh in the background so they don't see stale entitlements.
      void refetchPhysicalCardTxnsFeedFeature()
      redirect()
    }
  }

  const getDynamicPlanCtaAction = ({
    plan,
    planPeriod,
  }: {
    plan: (typeof sortedPlans)[number]
    planPeriod: 'MONTHLY' | 'YEARLY'
  }) => {
    const href =
      planPeriod === 'YEARLY' ? plan.ctaUrls?.yearly : plan.ctaUrls?.monthly

    return {
      href: href ?? undefined,
      rel: href ? 'noopener noreferrer' : undefined,
      loading: dynamicPricingLoadingPlanId === plan.id,
      onClick: async (e?: React.MouseEvent): Promise<void> => {
        analytics.track(`onboarding ${plan.id} tier selected`, {
          orgId: user?.organizationId,
          userId: user?.id,
        })

        // Dynamic Starter with flag on: intercept the default <a> nav and
        // create the Starter subscription server-side before opening the
        // Card Acknowledgements Modal. The Chargebee URL would normally
        // handle subscription creation, but we're short-circuiting it.
        const isDynamicStarter =
          plan.id.includes('starter') && !plan.id.includes('enterprise')
        if (isDynamicStarter && shouldShowAckModalOnSuccess) {
          e?.preventDefault()
          if (!user?.organizationId) return
          setPendingSaasTier('STARTER')
          setPendingBillingPeriod(planPeriod)
          setDynamicPricingLoadingPlanId(plan.id)
          postCreateSubscription(
            {
              orgId: user.organizationId,
              redirect: false,
              body: {
                billingPeriod: planPeriod,
                plan: 'STARTER',
                issuerId: pricingIssuerId,
              },
            },
            {
              onSuccess: () => {
                if (shouldShowAckModalOnSuccessRef.current) {
                  void openAckModalAfterSaasOrganizationUpdate()
                } else {
                  setDynamicPricingLoadingPlanId(null)
                  redirect()
                }
              },
              onError: () => {
                setDynamicPricingLoadingPlanId(null)
              },
            },
          )
          return
        }

        if (plan.ctaUrls === null && plan.id.includes('enterprise')) {
          if (!user?.organizationId) {
            return
          }

          setPendingSaasTier('ENTERPRISE')
          setPendingBillingPeriod(planPeriod)
          setDynamicPricingLoadingPlanId(plan.id)

          try {
            await sendPostEnterpriseRequest({
              orgId: user.organizationId,
              body: {
                intention: 'Onboarding tier selection - Web',
                issuerId: pricingIssuerId,
              },
            })

            if (shouldShowAckModalOnSuccessRef.current) {
              await openAckModalWithFeatureCheck()
            } else {
              redirect()
            }
          } finally {
            setDynamicPricingLoadingPlanId(null)
          }
          return
        }

        const isProTier = isProPricingPlan(plan)

        if (isProTier && activePromotion) {
          analytics.track('pricing promotion', {
            action: 'checkout_clicked',
            campaign_id: activePromotion.id,
            surface: 'authenticated_pricing',
            billing_cycle: planPeriod,
          })
        }

        // Already-Pro guard: if the user is on Pro and clicks any Pro tier
        // (re-click of the same period or a freq switch), skip Chargebee
        // entirely. Two shapes show up here, both broken:
        // 1. BE returns null `href` → button (wrapped in <div>) does nothing
        // 2. BE returns a `href` but Chargebee opens a stuck modal (no
        //    "Proceed to Checkout" button — the embedded checkout never
        //    completes for an already-Pro user)
        // Either way, navigate to /home so the click does something visible.
        // We route directly to `homeUrl()` (not via the local `redirect()`
        // helper) because the helper conditionally goes to a card detail
        // page when the cardId query param is present, which isn't the
        // intended behavior for this manage-subscription click path.
        if (isProTier && currentTier === 'PRO') {
          e?.preventDefault()
          dispatch(connectedReplace(homeUrl()))
          return
        }

        // Only route paid plans through the embedded modal.
        // - Starter: Chargebee completes instantly (no payment step)
        //   and navigates the iframe to its own redirect URL, so the
        //   success callback never fires — let the default <a> nav win.
        // - Enterprise: goes through the contact-sales request above.
        const isPaidPlan = !!href && isProPricingPlan(plan)
        if (isPaidPlan) {
          e?.preventDefault()
          setPendingSaasTier('PRO')
          setPendingBillingPeriod(planPeriod)
          openCheckoutUrl(href, {
            flow: 'create',
            entryPoint: 'pricing',
            dismissible: true,
            onSuccess: (): void => {
              void handleProCheckoutSuccess()
            },
          })
          return
        }

        // Defensive fallback: BE returned no checkout URL for a Pro tier and
        // we didn't catch it in the already-Pro guard above. The button is
        // wrapped in <div> when href is null, so without this the click is
        // silent. Navigate to /home directly (same rationale as above).
        if (!href && isProTier) {
          dispatch(connectedReplace(homeUrl()))
        }
      },
    }
  }

  // Show loading while dynamic pricing data is being fetched, or while the
  // onboarding subscription guard is still resolving (so we don't flash the
  // tiers before a possible redirect).
  if (
    !featuresIsFetched ||
    pricingPlansLoading ||
    isWaitingForSubscriptionGuard
  ) {
    return (
      <div
        css={style}
        className="PricingPage"
        data-testid="pricing-page-loading"
      >
        <div className="logo-container">
          <BrandPrimaryLogo />
        </div>
        <div className="SaasOnboardingPricingTiers-container flex-column justify-center align-center">
          <Processing />
        </div>
      </div>
    )
  }

  if (
    isPricingPageGateClosed ||
    shouldRedirectForExistingSubscription ||
    isRedirectingForSubscription
  ) {
    return <></>
  }

  return (
    <div css={style} className="PricingPage" data-testid="pricing-page">
      <div className="logo-container">
        <BrandPrimaryLogo />
      </div>
      <div className="SaasOnboardingPricingTiers-container flex-column justify-center align-center gap-10">
        {showDynamicPricingPage ? (
          <>
            <PricingPageHero
              translate={translate}
              partner={
                isNonExtendIssuer && pricingPlans?.partnerName
                  ? pricingPlans?.partnerName
                  : undefined
              }
            />
            {activePromotion && (
              <PromotionSavingsHero
                promotion={activePromotion}
                billingCycle={promotionBillingCycle}
                currencyFormatter={currencyFormatter}
                presentation={presentation}
              />
            )}
            <DynamicSaasOnboardingPricingTiers
              pricingPlans={sortedPlans}
              promotion={activePromotion ?? undefined}
              showVirtualCardAlert={pricingPlans?.showVCUnavailableAlert}
              isNonExtendIssuer={isNonExtendIssuer}
              currencyFormatter={currencyFormatter}
              translate={translate}
              getPlanCtaAction={getDynamicPlanCtaAction}
              onPlanPeriodChange={(plan, planPeriod): void => {
                if (!isProPricingPlan(plan)) return

                setPromotionBillingCycle(planPeriod)
                if (activePromotion) {
                  analytics.track('pricing promotion', {
                    action: 'billing_cycle_changed',
                    campaign_id: activePromotion.id,
                    surface: 'authenticated_pricing',
                    billing_cycle: planPeriod,
                  })
                }
              }}
              presentation={presentation}
            />
          </>
        ) : (
          <SaasOnboardingPricingTiers
            buttonLoadingStates={planLoadingStates}
            onClickPlanButton={async (planPeriod, planType): Promise<void> => {
              analytics.track(
                `onboarding ${planType.toLowerCase()} tier selected`,
                {
                  orgId: user?.organizationId,
                  userId: user?.id,
                },
              )
              switch (planType) {
                case 'STARTER':
                  if (shouldShowAckModalOnSuccess && user?.organizationId) {
                    setPendingSaasTier('STARTER')
                    setPendingBillingPeriod(planPeriod)
                    setPlanLoadingStates({
                      starter: true,
                      enterprise: false,
                      pro: false,
                    })

                    postCreateSubscription(
                      {
                        orgId: user.organizationId,
                        redirect: false,
                        body: {
                          billingPeriod: planPeriod,
                          plan: 'STARTER',
                          issuerId: pricingIssuerId,
                        },
                      },
                      {
                        onSuccess: () => {
                          if (shouldShowAckModalOnSuccessRef.current) {
                            void openAckModalAfterSaasOrganizationUpdate()
                          } else {
                            redirect()
                          }
                        },
                        onError: () => {
                          resetPlanLoadingStates()
                        },
                      },
                    )
                  } else if (user?.organizationId) {
                    setPlanLoadingStates({
                      starter: true,
                      enterprise: false,
                      pro: false,
                    })

                    postCreateSubscription(
                      {
                        orgId: user.organizationId,
                        redirect: false,
                        body: {
                          billingPeriod: planPeriod,
                          plan: 'STARTER',
                        },
                      },
                      {
                        onSuccess: () => {
                          redirect()
                        },
                      },
                    )
                  } else {
                    redirect()
                  }
                  break
                case 'PRO':
                  // By this time in the user flow, the user's org should be updated via Redux, so we can
                  // assume that if the `organizationSubscriptionUrlModel` is truthy, we can
                  // use those links and finish up the flow here
                  if (user?.organizationId) {
                    setPendingSaasTier('PRO')
                    setPendingBillingPeriod(planPeriod)
                    setPlanLoadingStates({
                      starter: false,
                      enterprise: false,
                      pro: true,
                    })
                    triggerCheckout(
                      {
                        type: 'create',
                        args: {
                          orgId: user.organizationId,
                          body: {
                            billingPeriod: planPeriod,
                            plan: 'PRO',
                          },
                        },
                      },
                      {
                        flow: 'create',
                        entryPoint: 'pricing',
                        dismissible: false,
                        onSuccess: (): void => {
                          void handleProCheckoutSuccess()
                        },
                      },
                    )
                  }
                  break
                case 'ENTERPRISE':
                  if (user?.organizationId) {
                    setPendingSaasTier('ENTERPRISE')
                    setPendingBillingPeriod(planPeriod)
                    setPlanLoadingStates({
                      starter: false,
                      enterprise: true,
                      pro: false,
                    })
                    await sendPostEnterpriseRequest({
                      orgId: user.organizationId,
                      body: {
                        intention: 'Onboarding tier selection - Web',
                        issuerId: pricingIssuerId,
                      },
                    }).catch((error) => {
                      // Oh no. An error happened.
                      throw error
                    })

                    if (shouldShowAckModalOnSuccessRef.current) {
                      await openAckModalWithFeatureCheck()
                    } else {
                      redirect()
                    }
                  }
                  break
              }
            }}
          />
        )}
      </div>
      {!showDynamicPricingPage && (
        <p className="font-support-medium asterisk-text align-center flex-column">
          {translate(
            '*Virtual card functionality for applicable bank partners',
          )}
        </p>
      )}
      <div className="footer flex-row justify-center">
        <BrandAgnosticFooter logOutOption topMargin={false} />
      </div>
      {renderCheckout()}
      {/* 
        This loading modal is to prevent a race condition on the backend to ensure the /features call 
        to get the physical card txn feed feature availability in later. Need a backend fix or 
        a better solution for this, but this seems to be working for now
      */}
      <BaseModal
        isOpen={showCheckoutLoadingModal}
        onClose={(): void => undefined}
        showCloseButton={false}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        aria-label={translate('Finishing checkout')}
      >
        <div
          className="checkout-loading-modal-content flex-column align-center justify-center gap-4"
          data-testid="checkout-loading-modal"
        >
          <Processing />
        </div>
      </BaseModal>
      {ackView === 'ack' && ackSubmissionData && (
        <ConnectPhysicalCardModal
          isOpen
          onClose={handleAckModalDismiss}
          dismissible={!astrada.onboarding}
          reviewEnrollCardProps={{
            submissionData: ackSubmissionData,
            onBack: handleAckModalDismiss,
            onVerifyComplete: handleAckVerifyComplete,
            saasTier: pendingSaasTier ?? undefined,
            billingCycle:
              pendingBillingPeriod === 'YEARLY'
                ? BillingCycle.YEARLY
                : pendingBillingPeriod === 'MONTHLY'
                  ? BillingCycle.MONTHLY
                  : undefined,
            hideBackButton: true,
            onUpdateCard: () => setAckView('edit'),
          }}
        />
      )}
      {ackView === 'edit' && ackSubmissionData && (
        <EditCardDetailsModal
          isOpen
          onClose={handleEditCardDismiss}
          submissionData={ackSubmissionData}
          onSubmit={handleEditCardSubmit}
        />
      )}
    </div>
  )
}
